import * as React from "react";
import { Eye, EyeOff, Check } from "lucide-react";
import { cn } from "../lib/cn";
import { Input, type InputProps } from "./Input";

/**
 * PasswordInput — a password field with a built-in reveal/hide toggle.
 *
 * Composes the base `Input` primitive (so it inherits every variant, size,
 * token color, and focus-ring) and layers on the two things every auth
 * surface re-implements by hand: a visibility toggle and an optional
 * "all good" success check.
 *
 * Why this exists: login, signup, and reset-password screens each need an
 * identical password field. Before this primitive, `AuthModal` hand-rolled
 * its own (twice, once per animation branch). This is the single reusable
 * atom those surfaces compose instead.
 *
 * Framework-agnostic by design — it forwards `ref` to the underlying input
 * and passes `value`/`onChange` straight through, so it drops into a
 * react-hook-form `<Controller>` (or plain controlled/uncontrolled use)
 * with no wrapper.
 *
 *   <PasswordInput aria-label="Password" autoComplete="current-password" />
 *   <PasswordInput value={pw} onValueChange={setPw} valid={isStrong} />
 *
 * Accessibility:
 * - The toggle is a real `<button type="button">` (never submits the form),
 *   carries an `aria-label` that flips with state, and exposes `aria-pressed`
 *   so screen readers announce reveal/hide.
 * - `tabIndex={-1}` keeps the toggle out of the Tab order — the field-then-
 *   submit flow stays uninterrupted, matching the platform's auth UX.
 */
export interface PasswordInputProps extends Omit<InputProps, "type"> {
  /** Render the reveal/hide visibility toggle. Default `true`. */
  showToggle?: boolean;
  /** aria-label for the toggle while the password is hidden. */
  revealLabel?: string;
  /** aria-label for the toggle while the password is visible. */
  hideLabel?: string;
  /**
   * When `true` and the field is non-empty, render a success check to the
   * left of the toggle. Wire this to your "password meets all rules" state.
   */
  valid?: boolean;
  /** Convenience callback firing the raw string value on every change. */
  onValueChange?: (value: string) => void;
}

export const PasswordInput = React.forwardRef<HTMLInputElement, PasswordInputProps>(
  (
    {
      className,
      showToggle = true,
      revealLabel = "Show password",
      hideLabel = "Hide password",
      valid,
      onValueChange,
      onChange,
      value,
      defaultValue,
      disabled,
      ...props
    },
    ref,
  ) => {
    const [visible, setVisible] = React.useState(false);

    // Track presence of a value so the success check only shows for a
    // non-empty field — works for both controlled and uncontrolled use.
    const isControlled = value !== undefined;
    const [hasValueUncontrolled, setHasValueUncontrolled] = React.useState(
      () => Boolean(defaultValue),
    );
    const hasValue = isControlled
      ? Boolean(String(value ?? ""))
      : hasValueUncontrolled;

    const showCheck = valid === true && hasValue;
    const controlCount = (showToggle ? 1 : 0) + (showCheck ? 1 : 0);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!isControlled) setHasValueUncontrolled(Boolean(event.target.value));
      onValueChange?.(event.target.value);
      onChange?.(event);
    };

    return (
      <div className="relative w-full">
        <Input
          ref={ref}
          type={visible ? "text" : "password"}
          value={value}
          defaultValue={defaultValue}
          disabled={disabled}
          onChange={handleChange}
          className={cn(
            controlCount === 2 && "pr-16",
            controlCount === 1 && "pr-10",
            className,
          )}
          {...props}
        />
        <div className="absolute inset-y-0 right-2 flex items-center gap-1">
          {showCheck && (
            <Check
              className="h-4 w-4 text-[hsl(var(--success))]"
              aria-hidden
            />
          )}
          {showToggle && (
            <button
              type="button"
              onClick={() => setVisible((v) => !v)}
              disabled={disabled}
              tabIndex={-1}
              aria-label={visible ? hideLabel : revealLabel}
              aria-pressed={visible}
              className={cn(
                "flex h-7 w-7 items-center justify-center rounded-md",
                "text-muted-foreground transition-colors hover:text-foreground",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                "disabled:pointer-events-none disabled:opacity-50",
              )}
            >
              {visible ? (
                <EyeOff className="h-4 w-4" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
            </button>
          )}
        </div>
      </div>
    );
  },
);

PasswordInput.displayName = "PasswordInput";
